import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';

import { CoachingSwitchboardService } from '../../../coaching-base/switchboard';
import { FocusShallow } from '../../../coaching-tags/tags/tags.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { DialogLayoutService } from '../dialog-layout.service';
import { TranslationKeyMap } from '../focus-selector.i18n';
import {
    Column,
    Context,
    Row,
    Sort,
    SortOrder,
} from '../grid/experimental.grid';
import { GridContent, SelectTemplateFn } from '../grid/experimental.ng.grid';
import { GridSortClickEvent } from '../grid/reusable-grid.model';
import { FocusSelectorBehaviourModel } from '../model/focus-selector-behaviour.model';
import { FocusSelectorFocusForPerformer } from '../model/focus-selector-focus-for-performer.model';
import {
    ColumnID,
    FocusSelectionGrid,
} from '../model/focus-selector-focus-selection.grid.model';
import { FocusSelectorFocusModel } from '../model/focus-selector-focus.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';
import { FocusSelectorSelectFocusEvent } from '../model/focus-selector-select-focus-event.model';
import { FocusSelectorSubBehaviourModel } from '../model/focus-selector-subbehaviour.model';

@Component({
    selector: 'cmps-focus-selection',
    templateUrl: './focus-selection.component.html',
    host: {
        class: 'cmps-focus-selection',
        '[class.row-mode]': '!_forceColumn',
    },
})
@TakeUntilDestroy()
export class FocusSelectionComponent implements OnInit, OnChanges {
    @Input() activeFocusesForPerformer: FocusSelectorFocusForPerformer[];
    @Input() activeMetric: FocusSelectorMetricModel;
    @Input() behaviours: FocusSelectorBehaviourModel[];
    @Input() gridData: FocusSelectionGrid;
    @Input() selectedFocus: FocusSelectorFocusModel;
    @Output() activeFocusBannerClick: EventEmitter<void>;
    @Output() selectSubBehaviourRow: EventEmitter<number>;
    @Output() sortFocusSelectionGrid: EventEmitter<GridSortClickEvent>;
    @Output() filterFocusSelectionGrid: EventEmitter<number>;
    @Output() selectFocus: EventEmitter<FocusSelectorSelectFocusEvent>;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _forceColumn: boolean;
    /** @private */ _dropdownBehaviours: IOption[];
    /** @private */ _ColumnID = ColumnID;
    /** @private */ _selectedBehaviour: FocusSelectorBehaviourModel;
    /** @private */ _selectedSubBehaviour: FocusSelectorSubBehaviourModel;
    /** @private */ _filterByBehaviourId: FocusSelectorBehaviourModel['id'];
    /** @private */ _SortOrder = SortOrder;

    /** @private */ _selectTpl: SelectTemplateFn = (
        context: Context,
        contents: QueryList<GridContent>,
    ) => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find(
                (e) =>
                    e.type === 'row' &&
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    e.id === (!context.ctx ? 'header' : context.ctx.templateId),
            );
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };

    constructor(
        private readonly _dialogLayoutService: DialogLayoutService,
        private readonly _translateService: TranslateService,
        private readonly _switchboardService: CoachingSwitchboardService,
    ) {
        this.activeFocusesForPerformer = [];
        this.activeFocusBannerClick = new EventEmitter();
        this.selectSubBehaviourRow = new EventEmitter();
        this.sortFocusSelectionGrid = new EventEmitter();
        this.filterFocusSelectionGrid = new EventEmitter();
        this.selectFocus = new EventEmitter();
    }

    ngOnInit(): void {
        this._dialogLayoutService.forceColumn
            .pipe(takeUntilDestroy(this))
            .subscribe((forceColumn) => {
                this._forceColumn = forceColumn;
            });

        this._setDropdownBehaviours();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.gridData != null) {
            this._filterByBehaviourId =
                this.gridData?.filterByBehaviourId ?? null;

            if (this.gridData.selection?.behaviourId != null) {
                this._selectBehaviour(
                    this.gridData.selection.behaviourId,
                    false,
                );
            }

            if (this.gridData.selection?.subBehaviourId != null) {
                this._selectSubBehaviour(
                    this.gridData.selection.subBehaviourId,
                );
            }
        }

        if (changes.selectedFocus != null) {
            if (this.selectedFocus?.behaviour?.id != null) {
                this._selectBehaviour(this.selectedFocus.behaviour.id, false);
            }

            if (this.selectedFocus?.subBehaviour?.id != null) {
                this._selectSubBehaviour(this.selectedFocus.subBehaviour.id);
            }
        }
    }

    private _setDropdownBehaviours(): void {
        const emptyLabel = this._translateService.instant(
            TranslationKeyMap._11093_FOCUS_SELECTION_PLACEHOLDER,
        );

        this._dropdownBehaviours = [
            { label: emptyLabel, value: null },
            ...this.behaviours.map((behaviour) => ({
                label: behaviour.name,
                value: behaviour.id,
            })),
        ];
    }

    /** @private */ _getColumnTitle(columnId: ColumnID): string {
        switch (columnId) {
            case this._ColumnID.RANK:
                return this._translateService.instant(
                    TranslationKeyMap._777_RANK,
                );
            case this._ColumnID.FOCUS:
                return this._translateService.instant(
                    TranslationKeyMap._7361_FOCUS,
                );
            case this._ColumnID.PROJECTED_15_DAY_IMPROVEMENT:
                return this._translateService.instant(
                    TranslationKeyMap._11099_15_DAY_FORECAST,
                );
            case this._ColumnID.PROJECTED_30_DAY_IMPROVEMENT:
                return this._translateService.instant(
                    TranslationKeyMap._11100_30_DAY_FORECAST,
                );
            case this._ColumnID.PROJECTED_60_DAY_IMPROVEMENT:
                return this._translateService.instant(
                    TranslationKeyMap._11101_60_DAY_FORECAST,
                );
            default:
                return '';
        }
    }

    /** @private */ _selectRow(selectedRows: Set<Row['id']>): void {
        const selected = Array.from(selectedRows)[0];
        this.selectSubBehaviourRow.emit(parseInt(selected));
    }

    private _findById<
        ModelType extends
            | FocusSelectorBehaviourModel
            | FocusSelectorSubBehaviourModel,
    >(list: ModelType[], id: number): ModelType {
        return list.find((behaviourOrSub) => behaviourOrSub.id === id);
    }

    /** @private */ _selectBehaviour(
        behaviourId: number,
        shouldEmit: boolean = true,
    ): void {
        if (
            this._selectedBehaviour != null &&
            this._selectedBehaviour.id !== behaviourId
        ) {
            this._selectedSubBehaviour = null;
        }

        this._selectedBehaviour = this._findById<FocusSelectorBehaviourModel>(
            this.behaviours,
            behaviourId,
        );

        if (this.activeMetric?.id == null) {
            this._filterByBehaviourId = behaviourId;
        }

        if (this.activeMetric?.id != null && shouldEmit) {
            this.filterFocusSelectionGrid.emit(behaviourId);
        }
    }

    /** @private */ _selectSubBehaviour(subBehaviourId: number): void {
        if (this._selectedBehaviour != null) {
            this._selectedSubBehaviour =
                this._findById<FocusSelectorSubBehaviourModel>(
                    this._selectedBehaviour.subBehaviours,
                    subBehaviourId,
                );
        }
    }

    /** @private */ _getSortDef(columnId: Column['id']): Sort {
        for (const sortDef of this.gridData.sortDefs) {
            if (sortDef.columnId === columnId) {
                return sortDef;
            }
        }

        console.warn('No sortDef found for column: ', columnId);
    }

    /** @private */ _selectFocus(): void {
        const activeCommitmentId = this._getActiveCommitmentId(
            this._selectedBehaviour?.id,
            this._selectedSubBehaviour?.id,
        );

        this.selectFocus.emit({
            behaviour: {
                id: this._selectedBehaviour?.id ?? null,
                name: this._selectedBehaviour?.name ?? '',
            },
            subBehaviour: {
                id: this._selectedSubBehaviour?.id ?? null,
                name: this._selectedSubBehaviour?.name ?? '',
            },
            activeCommitmentId,
        });
    }

    private _getActiveCommitmentId(
        behaviourId: number,
        tacticId: number,
    ): FocusSelectorFocusForPerformer['commitmentId'] {
        const focusShallow: FocusShallow = {
            behaviourId,
            subBehaviourId: tacticId,
        };

        const commitmentSummary =
            this._switchboardService.pillarTwo.sbActiveCommitmentWithFocus(
                focusShallow,
            );

        return commitmentSummary?.commitmentId ?? null;
    }
}
